import { useUserInfo, useUserSettings } from "@next-platform/client-common-core";
import { CharacterCardActions, type DropDownMenuItem } from "@next-platform/client-common-spicy-ui";
import type { Rulebook } from "@next-platform/packages-ts-types";
import React, { useCallback, useMemo } from "react";

import { ROUTES } from "@/constants";
import useLocalizedNavigation from "@/hooks/useLocalizedNavigation";
import RulebookListCard from "@/pages/RulebookMy/RulebookListCard";
import { AvatarPreset } from "@/types";
import { getAvatarUrl } from "@/utils/getAvatarUrl";

type CharacterCardActionsState = Partial<Record<CharacterCardActions, { isHidden?: boolean; isDisabled?: boolean; isLiked?: boolean }>>;

const getIsCoverBlurred = ({
  isAuthenticated,
  blurNSFW,
  coverIsNsfw,
}: {
  isAuthenticated: boolean;
  blurNSFW: boolean;
  coverIsNsfw?: boolean;
}): boolean => (!isAuthenticated || blurNSFW) && !!coverIsNsfw;

interface IRulebookCardProps {
  rulebook: Rulebook;
  navigate?: (path: string) => void;
  dropdownMenuActions?: DropDownMenuItem[];
  isPreview?: boolean;
  /** Optional handler for card actions wired up by the explore feed. Falls back to no-op. */
  characterCardActionsHandler?: (action: CharacterCardActions, characterId: string) => void;
  /** Optional state describing each card action (visibility, liked state). Falls back to all hidden. */
  characterCardActionsState?: CharacterCardActionsState;
}

const RulebookCard: React.FC<IRulebookCardProps> = ({ rulebook, dropdownMenuActions, isPreview = false, characterCardActionsHandler }) => {
  const { isAuthenticated } = useUserInfo();
  const { blurNSFW } = useUserSettings();
  const localizedNavigate = useLocalizedNavigation();
  const coverUrl = getAvatarUrl(rulebook.cover_url ?? "", { preset: AvatarPreset.IMAGE_500X650 }) ?? undefined;
  const isCoverBlurred = getIsCoverBlurred({ isAuthenticated, blurNSFW, coverIsNsfw: rulebook?.cover_is_nsfw ?? rulebook?.is_nsfw });
  const handleOpen = useCallback(() => {
    characterCardActionsHandler?.(CharacterCardActions.CHAT, rulebook.id);
    localizedNavigate(`${ROUTES.rulebook}/${rulebook.id}`);
  }, [characterCardActionsHandler, localizedNavigate, rulebook.id]);
  const meterChips = useMemo(() => rulebook.meter_chips ?? [], [rulebook.meter_chips]);
  const creatorPath = rulebook.creator_username ? `/creator/${rulebook.creator_username}` : undefined;
  const cardCreatorPath = isPreview ? undefined : creatorPath;
  const cardMenuActions = isPreview ? undefined : dropdownMenuActions;
  const cardOnOpen = isPreview ? undefined : handleOpen;

  return (
    <RulebookListCard
      id={rulebook.id}
      name={rulebook.name}
      description={rulebook.description}
      coverUrl={coverUrl}
      isCoverBlurred={isCoverBlurred}
      meterChips={meterChips}
      numHiddenMeters={rulebook.num_hidden_meters ?? 0}
      numRules={rulebook.num_rules ?? 0}
      numBots={rulebook.num_attached_characters ?? 0}
      numPersonas={rulebook.num_attached_personas ?? 0}
      isDefinitionVisible={rulebook.definition_visible}
      mode="public"
      creatorUsername={rulebook.creator_username}
      creatorPath={cardCreatorPath}
      menuActions={cardMenuActions}
      onOpen={cardOnOpen}
    />
  );
};

export default RulebookCard;
